import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Layers,
  Link2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";

const TransactionContext = ({ 
  transactions = [], 
  grouped = [], 
  summary = {},
  selectedDate,
  onSelectTransaction,
  loading = false 
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedTxns, setSelectedTxns] = useState(new Set());

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTransaction = (txnId) => {
    setSelectedTxns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txnId)) {
        newSet.delete(txnId);
      } else {
        newSet.add(txnId);
      }
      return newSet;
    });
  };

  const handleJournalSelected = () => {
    const selected = transactions.filter(t => selectedTxns.has(t.id));
    if (selected.length > 0) {
      onSelectTransaction(selected);
      setSelectedTxns(new Set());
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatOptionSymbol = (txn) => {
    if (txn.asset_type !== 'OPTION') return txn.symbol;
    
    const exp = txn.expiration_date 
      ? new Date(txn.expiration_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    const strike = txn.strike_price ? `$${txn.strike_price}` : '';
    const type = txn.put_call || '';
    
    return `${txn.underlying_symbol || txn.symbol} ${strike} ${type} ${exp}`.trim();
  };

  const formatPrice = (price, assetType) => {
    if (!price) return '--';
    const p = parseFloat(price);
    return assetType === 'OPTION' 
      ? `$${p.toFixed(2)}` 
      : `$${p.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-1/3 mb-4" />
        <div className="space-y-3">
          <div className="h-12 bg-slate-700 rounded" />
          <div className="h-12 bg-slate-700 rounded" />
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="bg-slate-800/30 rounded-xl border border-dashed border-slate-700 p-6 text-center">
        <Calendar className="mx-auto text-slate-600 mb-2" size={24} />
        <p className="text-slate-500 text-sm">
          No transactions on this date
        </p>
        <p className="text-slate-600 text-xs mt-1">
          Select a different date or add a manual entry
        </p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      
      {/* Header with Summary */}
      <div className="px-4 py-3 bg-slate-900/50 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-indigo-400" />
            <span className="text-sm font-medium text-white">
              Transactions on {new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
              {transactions.length} trades
            </span>
          </div>
          
          {summary.totalPnL !== undefined && summary.totalPnL !== 0 && (
            <div className={`flex items-center gap-1 text-sm font-bold ${summary.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {summary.totalPnL >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {summary.totalPnL >= 0 ? '+' : ''}{summary.totalPnL.toFixed(2)}
            </div>
          )}
        </div>
        
        {/* Quick Stats */}
        <div className="flex gap-4 mt-2 text-xs text-slate-400">
          {summary.tradesOpened > 0 && (
            <span className="flex items-center gap-1">
              <ArrowUpRight size={12} className="text-emerald-400" />
              {summary.tradesOpened} opened
            </span>
          )}
          {summary.tradesClosed > 0 && (
            <span className="flex items-center gap-1">
              <ArrowDownRight size={12} className="text-amber-400" />
              {summary.tradesClosed} closed
            </span>
          )}
          {summary.optionTrades > 0 && (
            <span>📊 {summary.optionTrades} options</span>
          )}
          {summary.stockTrades > 0 && (
            <span>📈 {summary.stockTrades} stocks</span>
          )}
        </div>
      </div>

      {/* Grouped Transactions */}
      <div className="divide-y divide-slate-700/50">
        {grouped.map((group) => (
          <div key={group.underlying} className="bg-slate-800/30">
            
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(group.underlying)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-white font-bold font-mono">
                  {group.underlying}
                </span>
                {group.hasOptions && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                    OPTIONS
                  </span>
                )}
                <span className="text-xs text-slate-500">
                  {group.transactions.length} leg{group.transactions.length > 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                {group.totalPnL !== 0 && (
                  <span className={`text-sm font-mono font-bold ${group.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {group.totalPnL >= 0 ? '+' : ''}{group.totalPnL.toFixed(2)}
                  </span>
                )}
                {expandedGroups[group.underlying] ? (
                  <ChevronUp size={16} className="text-slate-400" />
                ) : (
                  <ChevronDown size={16} className="text-slate-400" />
                )}
              </div>
            </button>

            {/* Expanded Transactions */}
            {expandedGroups[group.underlying] && (
              <div className="px-4 pb-3 space-y-2">
                {group.transactions.map((txn) => (
                  <div
                    key={txn.id}
                    onClick={() => toggleTransaction(txn.id)}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedTxns.has(txn.id) 
                        ? 'bg-indigo-500/20 border-indigo-500' 
                        : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'}
                    `}
                  >
                    <div className="flex items-start justify-between gap-4">
                      
                      {/* Left: Trade Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {/* Side Badge */}
                          <span className={`
                            text-xs font-bold px-2 py-0.5 rounded
                            ${txn.side === 'BUY' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-red-500/20 text-red-400'}
                          `}>
                            {txn.side}
                          </span>
                          
                          {/* Position Effect */}
                          <span className={`
                            text-xs px-2 py-0.5 rounded
                            ${txn.position_effect === 'OPENING' || txn.position_effect === 'OPEN'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-amber-500/20 text-amber-400'}
                          `}>
                            {txn.position_effect === 'OPENING' || txn.position_effect === 'OPEN' ? 'OPEN' : 'CLOSE'}
                          </span>
                          
                          {/* Time */}
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={10} />
                            {formatTime(txn.transaction_date)}
                          </span>
                        </div>

                        {/* Symbol */}
                        <div className="text-white font-mono text-sm">
                          {formatOptionSymbol(txn)}
                        </div>
                        
                        {/* Quantity & Price */}
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>×{Math.abs(txn.quantity)}</span>
                          <span>@ {formatPrice(txn.price, txn.asset_type)}</span>
                          {txn.broker_name && (
                            <span className="text-slate-500">{txn.broker_name}</span>
                          )}
                        </div>
                      </div>

                      {/* Right: P&L if closing */}
                      <div className="text-right">
                        {txn.tradePnL && (
                          <div className={`font-mono font-bold ${txn.tradePnL.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {txn.tradePnL.pnl >= 0 ? '+' : ''}{txn.tradePnL.pnl.toFixed(2)}
                            <span className="text-xs ml-1 opacity-75">
                              ({txn.tradePnL.pnlPercent >= 0 ? '+' : ''}{txn.tradePnL.pnlPercent.toFixed(1)}%)
                            </span>
                          </div>
                        )}
                        
                        {txn.openingTrade && (
                          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1 justify-end">
                            <Link2 size={10} />
                            Opened @ {formatPrice(txn.openingTrade.price, txn.asset_type)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Opening Trade Details (if closing) */}
                    {txn.openingTrade && expandedGroups[group.underlying] && (
                      <div className="mt-2 pt-2 border-t border-slate-700/50 text-xs text-slate-400">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Opened:</span>
                          <span>{new Date(txn.openingTrade.transaction_date).toLocaleDateString()}</span>
                          <span>@ {formatPrice(txn.openingTrade.price, txn.asset_type)}</span>
                          <span className="text-slate-500">|</span>
                          <span>{txn.tradePnL?.holdingPeriodDays || 0} days held</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Journal Selected Button */}
      {selectedTxns.size > 0 && (
        <div className="p-3 bg-slate-900 border-t border-slate-700">
          <button
            onClick={handleJournalSelected}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <DollarSign size={16} />
            Journal {selectedTxns.size} Trade{selectedTxns.size > 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionContext;
